import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy, Trash2, Download, Play, Square } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { debugLog, clearDebugLogs, addDebugListener } from '@/components/DebugConsole';
import { useState, useEffect } from 'react';

const DebugPage = () => {
  const [logs, setLogs] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Add listener for debug logs
    const removeListener = addDebugListener((newLog) => {
      if (newLog) {
        setLogs(prev => [...prev, newLog]);
      } else {
        setLogs([]);
      }
    });

    return removeListener;
  }, []);

  const handleStartRecording = () => {
    setIsRecording(true);
    clearDebugLogs();
    debugLog('INFO', 'Debug recording started - Ready to track referral flow');
    toast({
      title: "Recording Started",
      description: "Debug console is now recording. Start the referral flow to capture events."
    });
  };

  const handleStopRecording = () => {
    setIsRecording(false);
    debugLog('INFO', 'Debug recording stopped');
    toast({
      title: "Recording Stopped",
      description: "Debug recording has been stopped."
    });
  };

  const handleClear = () => {
    clearDebugLogs();
    toast({
      title: "Debug logs cleared",
      description: "All debug logs have been removed."
    });
  };

  const handleCopy = () => {
    const logText = logs.map(log => 
      `[${log.timestamp}] ${log.type.emoji} ${log.type.label}: ${log.message}${log.data ? '\n' + log.data : ''}`
    ).join('\n\n');
    
    navigator.clipboard.writeText(logText).then(() => {
      toast({
        title: "Debug logs copied",
        description: "All debug logs have been copied to clipboard."
      });
    });
  };

  const handleDownload = () => {
    const logData = {
      timestamp: new Date().toISOString(),
      session: 'referral-flow-debug',
      logs: logs.map(log => ({
        timestamp: log.timestamp,
        eventType: log.eventType,
        message: log.message,
        data: log.data ? JSON.parse(log.data) : null
      }))
    };
    
    const blob = new Blob([JSON.stringify(logData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `referral-flow-debug-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Debug logs downloaded",
      description: "Debug logs have been saved as JSON file."
    });
  };

  const getEventCounts = () => {
    const counts = {};
    logs.forEach(log => {
      counts[log.eventType] = (counts[log.eventType] || 0) + 1;
    });
    return counts;
  };

  const eventCounts = getEventCounts();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🐛 Referral Flow Debug Console</h1>
          <p className="text-gray-600">
            Track the complete referral signup flow: signup → add songs → go live → receive request → go offline
          </p>
        </div>

        {/* Control Panel */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="h-5 w-5" />
              Recording Controls
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-center">
              {!isRecording ? (
                <Button onClick={handleStartRecording} className="bg-green-600 hover:bg-green-700">
                  <Play className="h-4 w-4 mr-2" />
                  Start Recording
                </Button>
              ) : (
                <Button onClick={handleStopRecording} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                  <Square className="h-4 w-4 mr-2" />
                  Stop Recording
                </Button>
              )}
              
              <div className="flex gap-2">
                <Button onClick={handleCopy} variant="outline" size="sm">
                  <Copy className="h-4 w-4 mr-1" />
                  Copy Logs
                </Button>
                <Button onClick={handleDownload} variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </Button>
                <Button onClick={handleClear} variant="outline" size="sm">
                  <Trash2 className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              </div>
              
              <div className="ml-auto">
                <Badge variant={isRecording ? "default" : "secondary"} className={isRecording ? "bg-red-500" : ""}>
                  {isRecording ? "🔴 Recording" : "⏸️ Stopped"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Event Summary */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Event Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{eventCounts.REFERRAL_SIGNUP || 0}</div>
                <div className="text-sm text-blue-800">🎯 Signups</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{eventCounts.ADD_SONGS || 0}</div>
                <div className="text-sm text-green-800">🎵 Songs Added</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{eventCounts.GO_LIVE || 0}</div>
                <div className="text-sm text-red-800">🔴 Go Live</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{eventCounts.RECEIVE_REQUEST || 0}</div>
                <div className="text-sm text-purple-800">📨 Requests</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Debug Logs */}
        <Card>
          <CardHeader>
            <CardTitle>Debug Logs ({logs.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-96 border rounded-md bg-gray-50 overflow-y-auto">
              <div className="p-4 space-y-3">
                {logs.length === 0 ? (
                  <div className="text-center text-gray-500 py-12">
                    <div className="text-6xl mb-4">🔍</div>
                    <div className="text-xl mb-2">No debug logs yet</div>
                    <div className="text-sm">Click "Start Recording" and begin the referral flow</div>
                  </div>
                ) : (
                  logs.map((log) => (
                    <div key={log.id} className="bg-white p-4 rounded-lg border-l-4 border-l-blue-400 shadow-sm">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl">{log.type.emoji}</span>
                        <span className="font-semibold text-gray-800">{log.type.label}</span>
                        <span className="text-sm text-gray-500 ml-auto">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-gray-700 mb-2">{log.message}</div>
                      {log.data && (
                        <details className="text-sm">
                          <summary className="cursor-pointer text-gray-500 hover:text-gray-700 font-medium">
                            View Data
                          </summary>
                          <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-x-auto border">
                            {log.data}
                          </pre>
                        </details>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>How to Use</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-start gap-2">
                <span className="font-semibold text-blue-600">1.</span>
                <span>Click "Start Recording" to begin capturing debug events</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold text-blue-600">2.</span>
                <span>Open a new tab and start the referral signup flow</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold text-blue-600">3.</span>
                <span>Complete the full flow: signup → add songs → go live → receive request → go offline</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold text-blue-600">4.</span>
                <span>Watch the events appear in real-time in this console</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold text-blue-600">5.</span>
                <span>Copy or download the logs to share with the development team</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DebugPage;
